import { queryOptions, useQuery } from '@tanstack/react-query';
import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';

export interface IUserInUserToAssignCase {
  userId: string;
  fullname: string;
  profileImage: string | null;
  teams: ITeamInUserToAssignCase[];
}
export interface ITeamInUserToAssignCase {
  teamId: string | null;
  name: string;
}
export interface ITeamWithUsersInUserToAssignCase extends ITeamInUserToAssignCase {
  users: ISimpleUserInUserToAssignCase[];
}
export interface ISimpleUserInUserToAssignCase {
  userId: string;
  fullname: string;
  profileImage: string | null;
}
export type TUserToAssignCase = {
  users: IUserInUserToAssignCase[];
  teams: ITeamWithUsersInUserToAssignCase[];
};
export type TGetUserToAssignCaseResponse = ApiResponse<TUserToAssignCase>;

export const getUsersToAssignCase = async (): Promise<TGetUserToAssignCaseResponse> => {
  const url = `/chat/get-user-to-assign`;

  return axiosInstance.get(url);
};

export const GET_USERS_TO_ASSIGN_CASE_QUERY_KEY = 'get-users-to-assign-case';

export const getUsersToAssignCaseQueryOptions = <TData = TGetUserToAssignCaseResponse>(
  select?: (data: TGetUserToAssignCaseResponse) => TData
) => {
  return queryOptions({
    queryKey: [GET_USERS_TO_ASSIGN_CASE_QUERY_KEY],
    queryFn: () => getUsersToAssignCase(),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetUsersToAssignCaseQuery<TData>(options: {
  queryConfig?: QueryConfig<typeof getUsersToAssignCaseQueryOptions<TData>>;
  select: (data: TGetUserToAssignCaseResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetUsersToAssignCaseQuery(options: {
  queryConfig?: QueryConfig<typeof getUsersToAssignCaseQueryOptions<TGetUserToAssignCaseResponse>>;
}): ReturnType<typeof useQuery<TGetUserToAssignCaseResponse>>;

// Implementation
export function useGetUsersToAssignCaseQuery<TData = TGetUserToAssignCaseResponse>({
  queryConfig,
  select,
}: {
  queryConfig?: QueryConfig<typeof getUsersToAssignCaseQueryOptions<TData | TGetUserToAssignCaseResponse>>;
  select?: (data: TGetUserToAssignCaseResponse) => TData;
}) {
  return useQuery({
    ...getUsersToAssignCaseQueryOptions(select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetUserToAssignCaseResponse ? TGetUserToAssignCaseResponse : TData>>;
}
